import React, { useEffect, useState } from 'react';
import { BeatLoader } from 'react-spinners';
import { rectSortingStrategy } from '@dnd-kit/sortable';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import Sidebar from '../components/Sidebar';
import toast, { Toaster } from 'react-hot-toast';
import { API } from '../../api/Api';

// One section per category, each fetched with its own `category` parameter
// rather than a single pipe-separated call — sort_order is per category, so the
// lists stay independent all the way through to save.
const GROUP_CATEGORIES = ['Backsplash', 'Flooring', 'Countertops'];

const SortableColorCard = ({ color }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: color.id,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        width: '200px',
    };

    // The API hands back a ready-made absolute URL (note its spelling of
    // "Thumnnail"); fall back to building one only if it is missing.
    const thumb =
        color?.ThumnnailImage ||
        (color?.thumbnail_image
            ? `${process.env.REACT_APP_VISUAL_APP_IMAGE_BASE_URL || ''}${color.thumbnail_image}`
            : '');

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="bg-white border rounded-md p-1 shadow cursor-move"
        >
            {thumb ? (
                <img
                    src={thumb}
                    alt={color.name}
                    className="w-[200px] h-[70px] object-cover rounded"
                />
            ) : (
                <div className="w-[200px] h-[70px] rounded bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
                    No texture
                </div>
            )}

            <p className="mt-1 text-[12px] text-center font-medium truncate">{color.name}</p>
        </div>
    );
};

const Groups = () => {
    const token = localStorage.getItem('token');

    // Keyed by category so each section owns its own rows, loading flag and error.
    const [sections, setSections] = useState(() =>
        Object.fromEntries(
            GROUP_CATEGORIES.map((c) => [c, { rows: [], loading: true, error: '' }])
        )
    );
    // Which sections actually changed. Tracked per category so saving fires one
    // call for the section you touched instead of one for every category.
    const [dirty, setDirty] = useState({});
    const isOrderChanged = Object.values(dirty).some(Boolean);
    const [saving, setSaving] = useState(false);
    const [sortType, setSortType] = useState('default');

    /* ---------------- SORT ---------------- */
    // Sorts every section at once. Each one stays independently ordered, so A–Z
    // is applied within a category rather than across all three.
    const handleSortChange = (e) => {
        const value = e.target.value;
        setSortType(value);
        if (value === 'default') return;

        setSections((prev) => {
            const next = { ...prev };
            const changed = {};

            GROUP_CATEGORIES.forEach((category) => {
                const rows = next[category].rows;
                const sorted = [...rows].sort((a, b) =>
                    value === 'az'
                        ? (a.name || '').localeCompare(b.name || '')
                        : (b.name || '').localeCompare(a.name || '')
                );

                // A section already in this order has nothing to save — marking it
                // dirty would fire a pointless call for it.
                const moved = sorted.some((row, i) => row.id !== rows[i].id);
                if (moved) changed[category] = true;

                next[category] = { ...next[category], rows: sorted };
            });

            if (Object.keys(changed).length) {
                setDirty((d) => ({ ...d, ...changed }));
            }
            return next;
        });
    };

    /* ---------------- FETCH ---------------- */
    // Always via REACT_APP_API_URL. The host in the shared curl
    // (be-visualizer-tool-887aa0e11caa) serves the React build, not the API — it
    // answers this path with index.html, which looks like a 200 but isn't the
    // endpoint.
    const fetchCategory = async (category) => {
        const url = `${process.env.REACT_APP_API_URL}/visualizer/groupcolors`;

        setSections((prev) => ({
            ...prev,
            [category]: { ...prev[category], loading: true, error: '' },
        }));

        try {
            const response = await API(url, { category }, token, 'application/json');

            if (response?.Result === 1 && Array.isArray(response.Result_value)) {
                setSections((prev) => ({
                    ...prev,
                    [category]: { rows: response.Result_value, loading: false, error: '' },
                }));
            } else {
                setSections((prev) => ({
                    ...prev,
                    [category]: {
                        rows: [],
                        loading: false,
                        error: response?.Message || 'Unexpected response',
                    },
                }));
            }
        } catch (err) {
            setSections((prev) => ({
                ...prev,
                [category]: {
                    rows: [],
                    loading: false,
                    error: err?.response?.data?.error || err?.message || 'Request failed',
                },
            }));
        }
    };

    const fetchAll = () => {
        setDirty({});
        GROUP_CATEGORIES.forEach(fetchCategory);
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ---------------- REORDER ---------------- */
    const handleDragEnd = (category) => (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        setSections((prev) => {
            const rows = prev[category].rows;
            const oldIndex = rows.findIndex((c) => c.id === active.id);
            const newIndex = rows.findIndex((c) => c.id === over.id);
            if (oldIndex === -1 || newIndex === -1) return prev;

            return {
                ...prev,
                [category]: { ...prev[category], rows: arrayMove(rows, oldIndex, newIndex) },
            };
        });
        // Only this section moved, so only this one needs saving.
        setDirty((d) => ({ ...d, [category]: true }));
    };

    /* ---------------- SAVE ---------------- */
    const handleSaveOrder = async () => {
        // These rows are colours (id, name, thumbnail_image, sort_order,
        // category), so the colour-order endpoint is the right table — same
        // payload shape the Collections screen uses for its own ordering.
        const url = `${process.env.REACT_APP_API_URL}/visualizer/updatecolororder`;

        // Only the sections that actually moved. Reordering Backsplash used to
        // fire a call for Flooring and Countertops too, rewriting a sort_order
        // nobody touched. One call per changed category, matching how they were
        // fetched, so a failure in one does not silently reorder another.
        const targets = GROUP_CATEGORIES.filter(
            (c) => dirty[c] && sections[c].rows.length > 0
        );
        if (targets.length === 0) return;

        try {
            setSaving(true);
            const results = await Promise.all(
                targets.map((category) =>
                    API(url, { ids: sections[category].rows.map((r) => r.id).join(',') }, token)
                        .then((res) => ({ category, ok: res?.Result === 1 }))
                        .catch(() => ({ category, ok: false }))
                )
            );

            const failed = results.filter((r) => !r.ok).map((r) => r.category);
            const saved = results.filter((r) => r.ok).map((r) => r.category);

            // Clear only what saved, so a failed section stays dirty and can be
            // retried without re-sending the ones that already went through.
            if (saved.length) {
                setDirty((d) => {
                    const next = { ...d };
                    saved.forEach((c) => delete next[c]);
                    return next;
                });
            }

            if (failed.length === 0) {
                toast.success(`Order saved: ${saved.join(', ')}`);
            } else {
                toast.error(`Failed to save: ${failed.join(', ')}`);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="flex">
                <Sidebar />
                <Toaster position="top-right" />

                {/* pb-28 clears the fixed Save bar; pb-8 on the card stops the last
                    section's margin collapsing out of it and sitting on the edge. */}
                <div className="flex-1 p-6 bg-gray-100 min-h-screen pb-28">
                    <div className="bg-white rounded-lg shadow-md pb-8">
                        <div className="p-4 flex items-center justify-between">
                            <h2 className="text-2xl font-semibold text-teal-600">Groups</h2>

                            <select
                                value={sortType}
                                onChange={handleSortChange}
                                className="border rounded px-3 py-1 text-sm"
                            >
                                <option value="default">Select Order</option>
                                <option value="az">Order By(A-Z)</option>
                                <option value="za">Order BY(Z–A)</option>
                            </select>
                        </div>

                        <hr className="border-gray-200 mb-4" />

                        {GROUP_CATEGORIES.map((category) => {
                            const { rows, loading, error } = sections[category];

                            return (
                                <div key={category} className="mb-10">
                                    <h3 className="px-6 mb-3 text-lg font-semibold text-gray-700">
                                        {category}
                                        {!loading && !error && (
                                            <span className="ml-2 text-sm font-normal text-gray-500">
                                                ({rows.length})
                                            </span>
                                        )}
                                    </h3>

                                    {loading && (
                                        <div className="flex justify-center py-8">
                                            <BeatLoader color="#14b8a6" size={10} />
                                        </div>
                                    )}

                                    {!loading && error && (
                                        <p className="px-6 text-sm text-red-600">{error}</p>
                                    )}

                                    {!loading && !error && rows.length === 0 && (
                                        <p className="px-6 text-sm text-gray-500">No data found</p>
                                    )}

                                    {!loading && !error && rows.length > 0 && (
                                        <DndContext
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleDragEnd(category)}
                                        >
                                            <SortableContext
                                                items={rows.map((c) => c.id)}
                                                strategy={rectSortingStrategy}
                                            >
                                                <div
                                                    className="grid gap-4 px-6"
                                                    style={{
                                                        gridTemplateColumns: 'repeat(auto-fill, 200px)',
                                                        justifyContent: 'start',
                                                    }}
                                                >
                                                    {rows.map((color) => (
                                                        <SortableColorCard key={color.id} color={color} />
                                                    ))}
                                                </div>
                                            </SortableContext>
                                        </DndContext>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Save Button */}
            <div className="fixed bottom-0 left-0 md:left-64 right-0 bg-white border-t shadow-lg px-6 py-4 flex justify-end z-40">
                <button
                    onClick={handleSaveOrder}
                    disabled={!isOrderChanged || saving}
                    className="bg-gradient-to-r from-teal-400 to-teal-500 text-white px-8 py-2 rounded shadow
                        hover:from-teal-500 hover:to-teal-600
                        disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saving ? 'Saving...' : 'Save Order'}
                </button>
            </div>
        </>
    );
};

export default Groups;
